import { type ReactElement, useRef, useState } from "react";
import { useTimeAccounts } from "~api/timeAccounts.ts";
import { useFeatureGate } from "~api/useFeatureGate.ts";
import { useTranslation } from "~contexts/I18nContext/I18nContext";
import type { LeisureTimeAccount, VacationEntitlement } from "~generated";
import { AccountSummarySlide } from "./AccountSummarySlide";
import { LegacyVacationEntitlementCard } from "./LegacyVacationEntitlementCard";
import { LeisureTimeAccountCard } from "./LeisureTimeAccountCard";
import styles from "./SlideableTimeWidget.module.scss";
import { TimeWorkedSlide } from "./TimeWorkedSlide";
import { VacationSlide } from "./VacationSlide";

export function SlideableTimeWidget() {
  const t = useTranslation();
  const { data } = useTimeAccounts();
  const { enabled, settled } = useFeatureGate(
    "vacation_and_leisure_time_account",
  );
  const leisureTimeAccount = data?.leisure_time_account;
  const vacationEntitlement = data?.vacation_entitlement;

  const slides = [
    <TimeWorkedSlide key="time-worked" showHeading={settled && enabled} />,
    ...(settled
      ? accountSlides({
          breakdown: enabled,
          leisureTimeAccount,
          vacationEntitlement,
          leisureHeading: t.leisureTimeAccountHeading,
        })
      : []),
  ];

  const { carouselRef, activeSlide, handleScroll } = useCarouselActiveSlide();

  return (
    <div>
      <div
        ref={carouselRef}
        className={styles.carousel}
        onScroll={handleScroll}
      >
        {slides.map((slide, i) => (
          <div
            // biome-ignore lint/suspicious/noArrayIndexKey: slide order is stable
            key={i}
            className={styles.slide}
          >
            {slide}
          </div>
        ))}
      </div>

      {slides.length > 1 && (
        <div className={styles.dots} aria-hidden>
          {slides.map((_, i) => (
            <div
              // biome-ignore lint/suspicious/noArrayIndexKey: dot order is stable
              key={i}
              className={
                i === activeSlide
                  ? `${styles.dot} ${styles.dotActive}`
                  : styles.dot
              }
            />
          ))}
        </div>
      )}
    </div>
  );
}

function useCarouselActiveSlide() {
  const [activeSlide, setActiveSlide] = useState(0);
  const carouselRef = useRef<HTMLDivElement>(null);

  const handleScroll = () => {
    const el = carouselRef.current;
    if (!el) return;
    setActiveSlide(Math.round(el.scrollLeft / el.offsetWidth));
  };

  return { carouselRef, activeSlide, handleScroll };
}

type AccountSlidesProps = {
  leisureTimeAccount: LeisureTimeAccount | null | undefined;
  vacationEntitlement: VacationEntitlement | null | undefined;
};

function accountSlides({
  breakdown,
  leisureHeading,
  ...accounts
}: AccountSlidesProps & { breakdown: boolean; leisureHeading: string }) {
  return breakdown
    ? breakdownAccountSlides({ ...accounts, leisureHeading })
    : legacyAccountSlides(accounts);
}

function breakdownAccountSlides({
  leisureTimeAccount,
  vacationEntitlement,
  leisureHeading,
}: AccountSlidesProps & { leisureHeading: string }): ReactElement[] {
  return [
    ...(leisureTimeAccount != null
      ? [
          <AccountSummarySlide
            key="leisure-time-account"
            heading={leisureHeading}
          >
            <LeisureTimeAccountCard account={leisureTimeAccount} />
          </AccountSummarySlide>,
        ]
      : []),
    ...(vacationEntitlement != null
      ? [<VacationSlide key="vacation" vacation={vacationEntitlement} />]
      : []),
  ];
}

function legacyAccountSlides({
  leisureTimeAccount,
  vacationEntitlement,
}: AccountSlidesProps): ReactElement[] {
  if (leisureTimeAccount == null && vacationEntitlement == null) {
    return [];
  }

  return [
    <AccountSummarySlide key="accounts">
      {leisureTimeAccount != null && (
        <LeisureTimeAccountCard account={leisureTimeAccount} />
      )}
      {vacationEntitlement != null && (
        <LegacyVacationEntitlementCard vacation={vacationEntitlement} />
      )}
    </AccountSummarySlide>,
  ];
}
